import React from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { TrendingUp, TrendingDown, LineChart } from 'lucide-react';
import { formatCurrency } from '@/lib/utils';
import { ResponsiveContainer, ComposedChart, Bar, Line, Area, CartesianGrid, XAxis, YAxis, Tooltip, Legend } from 'recharts';

export const ForecastTab = ({ loading, forecastData }) => {
  if (loading) return <div className="text-center py-12">Loading forecast data...</div>;
  if (!forecastData) return null;

  return (
    <div className="space-y-6">
      {/* Forecast Summary */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="border-l-4 border-l-emerald-500">
          <CardContent className="pt-6">
            <p className="text-sm text-muted-foreground">Forecasted Revenue (6 months)</p>
            <p className="text-3xl font-bold text-emerald-600">{formatCurrency(forecastData.summary.total_forecasted)}</p>
            <p className="text-xs text-muted-foreground mt-1">Using {forecastData.methodology}</p>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-teal-500">
          <CardContent className="pt-6">
            <p className="text-sm text-muted-foreground">Avg Monthly Forecast</p>
            <p className="text-3xl font-bold text-teal-600">{formatCurrency(forecastData.summary.avg_monthly)}</p>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-blue-500">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Growth Trend</p>
                <p className="text-3xl font-bold text-blue-600">{forecastData.summary.growth_rate}%</p>
              </div>
              {forecastData.summary.trend === 'growing' ? (
                <TrendingUp className="w-10 h-10 text-emerald-500" />
              ) : (
                <TrendingDown className="w-10 h-10 text-red-500" />
              )}
            </div>
            <p className="text-xs text-muted-foreground mt-1 capitalize">{forecastData.summary.trend}</p>
          </CardContent>
        </Card>
      </div>

      {/* Forecast Chart */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <LineChart className="w-5 h-5" />
            Revenue Forecast (6 Months)
          </CardTitle>
          <CardDescription>
            Showing realistic, optimistic, and pessimistic scenarios with confidence levels
          </CardDescription>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={400}>
            <ComposedChart
              data={[
                ...forecastData.historical.map(h => ({ ...h, type: 'historical' })),
                ...forecastData.forecast.map(f => ({ month: f.month, actual: null, ...f, type: 'forecast' }))
              ]}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="month" />
              <YAxis tickFormatter={(v) => `AED ${(v / 1000).toFixed(0)}K`} />
              <Tooltip formatter={(value) => value ? formatCurrency(value) : '-'} />
              <Legend />
              <Bar dataKey="actual" name="Historical" fill="hsl(188, 64%, 38%)" radius={[4, 4, 0, 0]} />
              <Area type="monotone" dataKey="optimistic" name="Optimistic" fill="hsl(164, 62%, 90%)" stroke="hsl(164, 62%, 40%)" strokeDasharray="5 5" />
              <Line type="monotone" dataKey="realistic" name="Forecast" stroke="hsl(38, 95%, 55%)" strokeWidth={3} dot={{ r: 5 }} />
              <Area type="monotone" dataKey="pessimistic" name="Pessimistic" fill="hsl(4, 79%, 90%)" stroke="hsl(4, 79%, 55%)" strokeDasharray="5 5" />
            </ComposedChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      {/* Forecast Details Table */}
      <Card>
        <CardHeader>
          <CardTitle>Monthly Forecast Breakdown</CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Month</TableHead>
                <TableHead className="text-right">Pessimistic</TableHead>
                <TableHead className="text-right">Realistic</TableHead>
                <TableHead className="text-right">Optimistic</TableHead>
                <TableHead>Confidence</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {forecastData.forecast.map((f) => (
                <TableRow key={f.month}>
                  <TableCell className="font-medium">{f.month}</TableCell>
                  <TableCell className="text-right font-mono text-red-500">{formatCurrency(f.pessimistic)}</TableCell>
                  <TableCell className="text-right font-mono font-semibold">{formatCurrency(f.realistic)}</TableCell>
                  <TableCell className="text-right font-mono text-emerald-600">{formatCurrency(f.optimistic)}</TableCell>
                  <TableCell>
                    <Badge variant={f.confidence === 'high' ? 'default' : f.confidence === 'medium' ? 'secondary' : 'outline'}>
                      {f.confidence}
                    </Badge>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
};
